import React from 'react'
import {Box, Typography} from '@mui/material'
import DatePickerNew from 'src/components-new/inputs/DatePickerNew'
import TimePickerNew from 'src/components-new/inputs/TimePickerNew'
import CheckboxGroupNew from 'src/components-new/inputs/CheckboxGroupNew'
import RadioGroupNew from 'src/components-new/inputs/RadioGroupNew'
import TextFieldNew from 'src/components-new/inputs/TextFieldNew'
import {NEUTRAL_SCALE} from 'src/theme-new/tokens'
import UnitValueField from './UnitValueField'
import {FrequencyStepValue, FrequencyValue} from './secondaryTherapyFormUtils'
import {FREQUENCY_UNIT_OPTIONS, TOLERANCE_DAYS_UNIT_OPTIONS, WEEKDAY_OPTIONS} from '../../patient/tabs/therapy/therapyOptions'
import {SecondaryTherapyDataSchemaElement} from 'src/constants/secondaryTherapyFields'

// New-style equivalent of src/components/templates/TherapyDialogs/FrequencyFields.tsx

interface FrequencyStepProps {
  value: FrequencyStepValue
  onChange: (value: FrequencyStepValue) => void
  dataSchema: SecondaryTherapyDataSchemaElement[]
}

export default function FrequencyStep({value, onChange, dataSchema}: FrequencyStepProps): JSX.Element {
  const {frequency, startDate, endDate} = value
  const set = (patch: Partial<FrequencyStepValue>): void => onChange({...value, ...patch})
  const setFrequency = (patch: Partial<FrequencyValue>): void => set({frequency: {...frequency, ...patch}})
  const isDaysSelectorEnabled = frequency.each?.unitType === 'WEEK'
  // "Ripeti nei giorni" can't have more days checked than "Numero assunzioni": you can't take the
  // drug more days in a week than the number of times you take it that week.
  const maxDays = isDaysSelectorEnabled ? frequency.times : undefined

  const isFieldVisible = (key: string): boolean => dataSchema.some((element) => element.value === key)
  // "Ogni" non ha una voce propria nel catalogo: è il complemento di "Numero assunzioni" (esprime
  // "N volte ogni M giorni/settimane/mesi"), quindi segue la sua stessa visibilità.
  const isTimesVisible = isFieldVisible('frequency.times')
  const fieldLabel = (key: string, fallback: string): string => dataSchema.find((element) => element.value === key)?.name ?? fallback

  const isStartDateVisible = isFieldVisible('startDate')
  const isEndDateVisible = isFieldVisible('endDate')

  return (
    <Box>
      {(isStartDateVisible || isEndDateVisible) && (
        <>
          <Typography sx={{fontWeight: 700, fontSize: 14, color: NEUTRAL_SCALE[900], mb: 1.5}}>
            Periodo terapia
          </Typography>
          {/* startDate/endDate are the therapy's own validity period. Both fields are optional. */}
          <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', sm: 'repeat(2, 1fr)'}, gap: 2, mb: 3}}>
            {isStartDateVisible && (
              <DatePickerNew
                label={fieldLabel('startDate', 'Data inizio terapia')}
                value={startDate}
                onChange={(v) => set({startDate: v})}
                maxDate={endDate}
              />
            )}
            {isEndDateVisible && (
              <DatePickerNew
                label={fieldLabel('endDate', 'Data fine terapia')}
                value={endDate}
                onChange={(v) => set({endDate: v})}
                minDate={startDate}
              />
            )}
          </Box>
        </>
      )}

      <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', sm: 'repeat(2, 1fr)'}, gap: 2}}>
        {isFieldVisible('frequency.asNeeded') && (
          <RadioGroupNew
            label={fieldLabel('frequency.asNeeded', 'Al bisogno')}
            value={frequency.asNeeded ? 'yes' : 'no'}
            onChange={(v) => setFrequency({asNeeded: v === 'yes'})}
            options={[{value: 'yes', label: 'Sì'}, {value: 'no', label: 'No'}]}
          />
        )}

        {!frequency.asNeeded && (
          <>
            {isTimesVisible && (
              <TextFieldNew
                label={fieldLabel('frequency.times', 'Numero assunzioni')}
                required
                fullWidth
                value={frequency.times !== undefined && frequency.times !== null ? String(frequency.times) : ''}
                onChange={(v) => {
                  const times = v === '' ? undefined : Number(v)
                  setFrequency({
                    times,
                    // Lowering the number of intakes below the already-checked day count trims the
                    // extra days rather than leaving a now-invalid selection in place.
                    ...(isDaysSelectorEnabled && times !== undefined && (frequency.days?.length ?? 0) > times
                      ? {days: frequency.days?.slice(0, times)}
                      : {}),
                  })
                }}
              />
            )}
            {isTimesVisible && (
              <UnitValueField
                label="Ogni"
                required
                value={frequency.each}
                onChange={(each) => setFrequency({
                  each,
                  // "Ripeti nei giorni" only applies to a weekly frequency: clear any previously
                  // selected days when switching away from WEEK, so a stale selection doesn't
                  // linger (still checked, just disabled) or get submitted with the new unit.
                  ...(each?.unitType !== 'WEEK' ? {days: []} : {}),
                })}
                unitOptions={FREQUENCY_UNIT_OPTIONS}
              />
            )}
            {isFieldVisible('frequency.days') && (
              <CheckboxGroupNew
                label={fieldLabel('frequency.days', 'Ripeti nei giorni')}
                value={frequency.days ?? []}
                onChange={(days) => setFrequency({days})}
                options={WEEKDAY_OPTIONS}
                disabled={!isDaysSelectorEnabled}
                maxSelected={maxDays}
                helperText={maxDays ? `Puoi selezionare al massimo ${maxDays} giorni in base al numero di assunzioni` : undefined}
              />
            )}
            {isFieldVisible('frequency.notificationTime') && (
              <TimePickerNew
                label={fieldLabel('frequency.notificationTime', 'Orario')}
                value={frequency.notificationTime ?? null}
                onChange={(notificationTime) => setFrequency({notificationTime})}
              />
            )}
            {isFieldVisible('frequency.toleranceDays') && (
              <UnitValueField
                label={fieldLabel('frequency.toleranceDays', 'Giorni di tolleranza')}
                value={frequency.toleranceDays}
                onChange={(toleranceDays) => setFrequency({toleranceDays})}
                unitOptions={TOLERANCE_DAYS_UNIT_OPTIONS}
              />
            )}
          </>
        )}
      </Box>
    </Box>
  )
}
